import { API_ENDPOINTS } from '../config/api';

const API_BASE_URL = API_ENDPOINTS.PLAID.BASE;

class PlaidService {
  constructor() {
    this.userId = 'demo_user'; // In production, this would come from user auth
  }

  /**
   * Check if user has connected bank accounts
   */
  async checkConnectionStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/status?userId=${this.userId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to check connection status');
      }
      
      return data;
    } catch (error) {
      console.error('Error checking connection status:', error);
      return { connected: false, error: error.message };
    }
  }

  /**
   * Get connected accounts
   */
  async getAccounts() {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts?userId=${this.userId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch accounts');
      }
      
      return data.accounts;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      throw error;
    }
  }

  /**
   * Get transactions
   */
  async getTransactions(startDate = null, endDate = null) {
    try {
      let url = `${API_BASE_URL}/transactions?userId=${this.userId}`;
      
      if (startDate) {
        url += `&startDate=${startDate}`;
      }
      if (endDate) {
        url += `&endDate=${endDate}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch transactions');
      }
      
      return data.transactions;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Get liabilities (credit cards, loans, etc.)
   */
  async getLiabilities() {
    try {
      const response = await fetch(`${API_BASE_URL}/liabilities?userId=${this.userId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch liabilities');
      }
      
      return data.liabilities;
    } catch (error) {
      console.error('Error fetching liabilities:', error);
      throw error;
    }
  }

  /**
   * Disconnect bank account
   */
  async disconnectAccount() {
    try {
      const response = await fetch(`${API_BASE_URL}/item`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: this.userId }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect account');
      }
      
      return data;
    } catch (error) {
      console.error('Error disconnecting account:', error);
      throw error;
    }
  }

  /**
   * Get all financial data (accounts + transactions + liabilities)
   */
  async getAllData() {
    try {
      const status = await this.checkConnectionStatus();
      
      if (!status.connected) {
        return {
          connected: false,
          accounts: [],
          transactions: [],
          liabilities: [],
          message: 'No bank accounts connected'
        };
      }

      // Fetch all data in parallel
      const [accounts, transactions, liabilities] = await Promise.all([
        this.getAccounts().catch(() => []),
        this.getTransactions().catch(() => []),
        this.getLiabilities().catch(() => [])
      ]);

      return {
        connected: true,
        accounts,
        transactions,
        liabilities,
        connectedAt: status.connectedAt
      };
    } catch (error) {
      console.error('Error fetching all data:', error);
      throw error;
    }
  }
}

const plaidServiceInstance = new PlaidService();
export default plaidServiceInstance; 